import { FC, forwardRef, useImperativeHandle, useState } from 'react';
import { Button, Form, Space, Spin } from 'antd';
import V2Container from '@/common/components/Data/V2Container';
import { useMethods } from '@lhb/hook';
import styles from './entry.module.less';
import { contractUpdate, createShop, getChancePointShopDetail, getShopDetail, getShopListColumn, shopUpdate, youthMapCreateShop } from '@/common/api/expandStore/shop';
import EditMain from './components/EditMain';
import V2Title from '@/common/components/Feedback/V2Title';
import V2Message from '@/common/components/Others/V2Hint/V2Message';
import moment from 'moment';
import { approvalTemplateList } from '@/common/api/approvalflow';
import ApprovalCreateDialog from '@/common/components/business/ExpandStore/ApprovalCreateDialog';
import { isArray, isNotEmptyAny } from '@lhb/func';
import { ApprovalType, ApprovalTypeValue } from '@/common/components/business/ExpandStore/ts-config';
import { ShopEditType } from './ts-config';
import { LngLat } from '@/common/components/Map/V2AMap';
import { getTargetPOIInfo } from '@/common/api/spotevaluation';
import { showInvalidFieldMsg } from '@/common/utils/ways';

/** 门店编辑抽屉 */
const Edit: FC<{
  id,
  taskId?,
  onClose?,
  reload?,
  editType?,
  ref?,
}> = forwardRef(({ id, taskId, onClose, reload, editType }, ref) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [requesting, setRequesting] = useState<boolean>(false);
  const [detail, setDetail] = useState<any>({});
  const [innerMainHeight, setInnerMainHeight] = useState<number>(0);
  const [enableContractApproval, setEnableContractApproval] = useState<boolean>(false);
  const [approvalVisible, setApprovalVisible] = useState<boolean>(false);
  const [approvalParams, setApprovalParams] = useState<any>({});
  const [addressInfo, setAddressInfo] = useState<any>({}); // 省市区id获取用
  const [mapPoiInfo, setMapPoiInfo] = useState<any>({}); // 当前地图查询到的poi信息

  const title = [ShopEditType.Create, ShopEditType.MapCreate].includes(editType) ? '门店创建' : (ShopEditType.Edit ? '门店详情' : '合同签约');

  useImperativeHandle(ref, () => ({
    init: methods.init
  }));

  const methods = useMethods({

    /**
     * @description 初始化
     */
    init(lngLat:LngLat) {
      if ([ShopEditType.Create, ShopEditType.MapCreate].includes(editType)) {
        methods.loadConfig();
        lngLat && methods.loadPOIData(lngLat);
        return;
      }

      if (!id) {
        return;
      }
      methods.loadData(id);
      (editType === ShopEditType.Sign) && methods.loadApprovalTemplates(); // 签约场景事先读取审批模板
    },

    /** 创建场景 获取配置 */
    async loadConfig() {
      setLoading(true);
      const data = await getShopListColumn({});
      setLoading(false);
      isNotEmptyAny(data) && setDetail({ configs: data }); // 与原先的编辑场景详情格式兼容
    },

    /** 编辑/签约场景 获取详情 */
    async loadData(id: number) {
      setLoading(true);
      const api = editType === ShopEditType.Edit ? getShopDetail : getChancePointShopDetail;
      const data = await api({ id });
      setLoading(false);
      setDetail(data);
    },

    /**
     * @description 加载所有审批模板
     */
    loadApprovalTemplates() {
      approvalTemplateList().then((data: any) => {
        let enable = false;
        if (isArray(data) && data.length) {
          const targetData = data.find(itm => itm.typeValue === ApprovalTypeValue.Contract);
          enable = !!targetData?.enable && !!targetData.approvalFlowId;
        }
        setEnableContractApproval(enable);
      });
    },

    onCancel() {
      form?.resetFields();
      onClose?.();
    },
    onSubmit() {
      form.validateFields().then((values) => {
        if (requesting) return;

        // 防止地图创建时未搜到poi
        const getIdValue = (mapPoiId: number | undefined, addressId: number | undefined) => {
          return editType === ShopEditType.MapCreate
            ? (mapPoiId ?? addressId)
            : addressId;
        };

        // 目前已去除编辑时“门店微调不得跨区”的限制），各场景下都需要提交省市区id参数
        values.provinceId = getIdValue(mapPoiInfo?.provinceId, addressInfo?.provinceId);
        values.cityId = getIdValue(mapPoiInfo?.cityId, addressInfo?.cityId);
        values.districtId = getIdValue(mapPoiInfo?.districtId, addressInfo?.districtId);

        if ([ShopEditType.Edit, ShopEditType.Sign].includes(editType)) {
          values.id = detail.id;
        }

        delete (values.cityName);
        delete (values.districtName);
        delete (values.provinceName);

        if ('contractEnd' in values) {
          values.contractEnd = values.contractEnd ? moment(values.contractEnd).format('YYYY-MM-DD') : null;
        }
        if ('contractStart' in values) {
          values.contractStart = values.contractStart ? moment(values.contractStart).format('YYYY-MM-DD') : null;
        }
        if ('openDate' in values) {
          values.openDate = values.openDate ? moment(values.openDate).format('YYYY-MM-DD') : null;
        }
        if ('signDate' in values) {
          values.signDate = values.signDate ? moment(values.signDate).format('YYYY-MM-DD') : null;
        }
        if ('managerName' in values) {
          const managerNameVal = values.managerName;
          values.managerId = managerNameVal?.[0]?.value;
          values.managerName = managerNameVal?.[0]?.label;
        }
        if ('investmentManagerName' in values) {
          const investmentManagerNameVal = values.investmentManagerName;
          values.investmentManagerId = investmentManagerNameVal?.[0]?.value;
          values.investmentManagerName = investmentManagerNameVal?.[0]?.label;
        }
        if ([ShopEditType.Create, ShopEditType.MapCreate].includes(editType)) { // 创建场景
          setRequesting(true);
          const post = editType === ShopEditType.Create ? createShop : youthMapCreateShop; // 地图上新建使用
          post(values).then((res) => {
            V2Message.success('新增成功');
            onClose?.();
            reload?.(res);
          }).finally(() => {
            setRequesting(false);
          });
        } else if ((editType === ShopEditType.Sign) && enableContractApproval) { // 签约场景，且启用了合同审批流，执行发起审批
          setApprovalParams(values);
          setApprovalVisible(true);
        } else { // 签约且未配置审批流，直接签约
          setRequesting(true);
          const api = editType === ShopEditType.Edit ? shopUpdate : contractUpdate;

          const _params = {
            ...values,
            ...(taskId ? { standardTaskId: taskId } : {})
          };
          api(_params).then((res) => {
            V2Message.success(editType === ShopEditType.Edit ? '编辑成功' : '签约成功');
            onClose?.();
            reload?.(res);
          }).finally(() => {
            setRequesting(false);
          });
        }
      }, (err: any) => {
        err && showInvalidFieldMsg(err.errorFields, 1);
      });
    },
    loadPOIData: async (lnglat: LngLat) => {
      const params = { lng: lnglat[0], lat: lnglat[1] };
      const data = await getTargetPOIInfo(params);
      if (!data) return;
      const { provinceName, cityName, districtName, provinceId, cityId, districtId, latitude, longitude, address } = data;
      // 没有地址，没有城市，基本上在大海里
      if (!address) {
        V2Message.warning('未查询到对应的POI点位');
        return;
      }
      +latitude && +longitude && setMapPoiInfo({
        lng: +longitude,
        lat: +latitude,
        address: `${provinceName}${cityName}${districtName}${address}`,
        provinceName,
        cityName,
        districtName,
        provinceId,
        cityId,
        districtId,
      });
    },
  });

  return (<>
    <Spin tip='数据正在加载中请稍等......' spinning={loading}>
      <V2Container
        emitMainHeight={(h) => setInnerMainHeight(h)}
        extraContent={{
          top: <V2Title type='H1' text={title} className={styles.title} />,
          bottom: (<Space className={styles.bottom} size={24}>
            <Button type='default' onClick={methods.onCancel}>取消</Button>
            <Button type='primary' loading={requesting} onClick={methods.onSubmit}>确定</Button>
          </Space>)
        }}
        style={{ height: '100vh', width: '100%' }}
        className={styles.container}>
        <EditMain
          form={form}
          detail={detail}
          editType={editType}
          mapPoiInfo={mapPoiInfo}
          setAddressInfo={setAddressInfo}
          style={{
            height: innerMainHeight || 'auto',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}/>
      </V2Container>
      <ApprovalCreateDialog
        visible={approvalVisible}
        setVisible={setApprovalVisible}
        id={id}
        type={ApprovalType.Contract}
        typeValue={ApprovalTypeValue.Contract}
        specialParams={{
          chancePointContractFromData: approvalParams,
          ...(taskId ? { standardTaskId: taskId } : {})
        }}
        onComplete={() => {
          onClose?.();
          reload?.();
        }}/>
    </Spin>
  </>);
});

export default Edit;
